import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search, Globe, MapPin, Shield, Clock, Plane, Filter, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MarketingHeader from "@/components/layout/MarketingHeader";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/seo/SEO";
import { supabase } from "@/integrations/supabase/client";
import { getCountryDisplayName } from "@/utils/countryTranslations";

interface Country {
  id: string;
  name: string;
  tagline: string | null;
  flag_url: string | null;
  image_url: string | null;
  banner_url: string | null;
  risk_level: string | null;
  timezone_utc_offset: string | null;
  tourist_stay_default: string | null;
  remote_work_visa_option: string | null;
  cost_of_living: number | null;
  attractiveness_rating: number | null;
  ease_compliance: number | null;
}

const CountryGuides = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [riskFilter, setRiskFilter] = useState<string>("all");
  const [regionFilter, setRegionFilter] = useState<string>("all");

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCountries = countries.filter(country => {
    const displayName = getCountryDisplayName(country.name, i18n.language);
    const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         country.tagline?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRisk = riskFilter === "all" || country.risk_level?.toLowerCase() === riskFilter;
    return matchesSearch && matchesRisk;
  });

  const getRiskBadge = (riskLevel: string | null) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low':
        return <Badge className="bg-success text-success-foreground">{t("countryGuides.risk.low", "Low Risk")}</Badge>;
      case 'medium':
        return <Badge className="bg-warning text-warning-foreground">{t("countryGuides.risk.medium", "Medium Risk")}</Badge>;
      case 'high':
        return <Badge variant="destructive">{t("countryGuides.risk.high", "High Risk")}</Badge>;
      default:
        return <Badge variant="outline">{t("countryGuides.risk.unknown", "Unknown")}</Badge>;
    }
  };

  const featuredCountries = countries.filter(c => 
    ['France', 'Spain', 'Portugal', 'Germany', 'United Kingdom', 'Italy'].includes(c.name)
  ).slice(0, 6);

  return (
    <>
      <SEO 
        title={t("countryGuides.seo.title", "Country Guides - Remote Work Destinations | Renest")}
        description={t("countryGuides.seo.description", "Explore comprehensive guides for remote work destinations worldwide. Learn about visa requirements, tax implications, and compliance.")}
      />
      <div className="min-h-screen bg-background">
        <MarketingHeader />

        {/* Hero Section */}
        <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-4">
              <Globe className="h-3 w-3 mr-1" />
              {t("countryGuides.badge", "50+ Destinations")}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              {t("countryGuides.title", "Remote Work Country Guides")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              {t("countryGuides.subtitle", "Everything you need to know about working remotely from any destination. Visa requirements, tax rules, and compliance tips.")}
            </p>

            {/* Search and Filter */}
            <div className="max-w-2xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t("countryGuides.searchPlaceholder", "Search countries...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={riskFilter} onValueChange={setRiskFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder={t("countryGuides.filterRisk", "Risk Level")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("countryGuides.filters.all", "All Risk Levels")}</SelectItem>
                    <SelectItem value="low">{t("countryGuides.filters.low", "Low Risk")}</SelectItem>
                    <SelectItem value="medium">{t("countryGuides.filters.medium", "Medium Risk")}</SelectItem>
                    <SelectItem value="high">{t("countryGuides.filters.high", "High Risk")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Countries */}
        {!searchTerm && riskFilter === "all" && featuredCountries.length > 0 && (
          <section className="py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold">
                    {t("countryGuides.featured.title", "Popular Destinations")}
                  </h2>
                  <p className="text-muted-foreground">
                    {t("countryGuides.featured.subtitle", "Most requested remote work destinations by our clients")}
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredCountries.map((country) => (
                  <Card 
                    key={country.id} 
                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/country-guides/${country.name.toLowerCase().replace(/\s+/g, '-')}`)}
                  >
                    <div className="aspect-video bg-muted relative overflow-hidden">
                      {country.banner_url || country.image_url ? (
                        <img 
                          src={country.banner_url || country.image_url || ''} 
                          alt={getCountryDisplayName(country.name, i18n.language)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                          <Globe className="h-16 w-16 text-primary/40" />
                        </div>
                      )}
                      <div className="absolute top-3 right-3">
                        {getRiskBadge(country.risk_level)}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-lg">
                            {getCountryDisplayName(country.name, i18n.language)}
                          </h3>
                          {country.tagline && (
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {country.tagline}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {country.timezone_utc_offset && (
                          <Badge variant="outline" className="text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {country.timezone_utc_offset}
                          </Badge>
                        )}
                        {country.tourist_stay_default && (
                          <Badge variant="outline" className="text-xs">
                            <Plane className="h-3 w-3 mr-1" />
                            {country.tourist_stay_default}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Countries Grid */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold">
                  {searchTerm || riskFilter !== "all" 
                    ? t("countryGuides.searchResults", "Search Results")
                    : t("countryGuides.allCountries", "All Countries")
                  }
                </h2>
                <p className="text-muted-foreground">
                  {t("countryGuides.showingCount", "Showing {{count}} destinations", { count: filteredCountries.length })}
                </p>
              </div>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-4">
                      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredCountries.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">
                    {t("countryGuides.noResults.title", "No countries found")}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {t("countryGuides.noResults.description", "Try adjusting your search or filters")}
                  </p>
                  <Button variant="outline" onClick={() => { setSearchTerm(""); setRiskFilter("all"); }}>
                    {t("countryGuides.noResults.clearFilters", "Clear Filters")}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {filteredCountries.map((country) => (
                  <Card 
                    key={country.id}
                    className="hover:border-primary transition-colors cursor-pointer group"
                    onClick={() => navigate(`/country-guides/${country.name.toLowerCase().replace(/\s+/g, '-')}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                            {country.flag_url ? (
                              <img src={country.flag_url} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <MapPin className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-medium group-hover:text-primary transition-colors">
                              {getCountryDisplayName(country.name, i18n.language)}
                            </h3>
                            <div className="flex items-center gap-2">
                              {country.risk_level && (
                                <span className={`text-xs ${
                                  country.risk_level.toLowerCase() === 'low' ? 'text-success' :
                                  country.risk_level.toLowerCase() === 'medium' ? 'text-warning' :
                                  'text-destructive'
                                }`}>
                                  ● {country.risk_level}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">
              {t("countryGuides.cta.title", "Need help with compliance?")}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              {t("countryGuides.cta.description", "Our team of experts can help you navigate the complexities of international remote work regulations.")}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button onClick={() => navigate('/contact')}>
                {t("countryGuides.cta.contact", "Talk to an Expert")}
              </Button>
              <Button variant="outline" onClick={() => navigate('/company-registration')}>
                {t("countryGuides.cta.getStarted", "Get Started Free")}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CountryGuides;
